import { BarChart, Badge, Card, Kpi, Loaded, Page, Table } from '../components/ui'
import { get } from '../lib/api'
import { count, percent, titleCase } from '../lib/format'
import { useResource } from '../lib/useResource'

type Fleet = {
  capsules: {
    total: number
    offline_24h: number
    firmware: Record<string, number>
    battery: Record<string, number>
  }
  bands: {
    total: number
    offline_24h: number
    firmware: Record<string, number>
    battery: Record<string, number>
  }
  other_devices: Record<string, number>
  providers: Record<
    string,
    { connected: number; disabled: number; expiring: number; erroring: number; stale: number }
  >
}

export function Devices() {
  const fleet = useResource((signal) => get<Fleet>('/v1/admin/devices', undefined, signal))

  return (
    <Page
      title="Devices & sources"
      subtitle="The SEYON Capsule fleet, plus the health of every third-party connector. Hardware identifiers are PII and are not listed here — per-device detail lives on the account."
    >
      <Loaded resource={fleet}>
        {(data) => {
          const providers = Object.entries(data.providers)
          return (
            <>
              <div className="grid grid--kpi">
                <Kpi label="Capsules paired" value={count(data.capsules.total)} />
                <Kpi
                  label="Offline · 24 h"
                  value={count(data.capsules.offline_24h)}
                  tone={data.capsules.offline_24h > 0 ? 'orange' : undefined}
                  foot={
                    data.capsules.total
                      ? `${percent(data.capsules.offline_24h / data.capsules.total)} of the fleet`
                      : undefined
                  }
                />
                <Kpi
                  label="Battery critical"
                  value={count(data.capsules.battery.critical ?? 0)}
                  tone={(data.capsules.battery.critical ?? 0) > 0 ? 'alert' : undefined}
                  foot="Under 15% — a Capsule that dies stops the ECG"
                />
                <Kpi
                  label="Wristbands paired"
                  value={count(data.bands.total)}
                  foot={
                    data.bands.offline_24h
                      ? `${count(data.bands.offline_24h)} not seen in 24 h`
                      : undefined
                  }
                />
                <Kpi
                  label="Band battery critical"
                  value={count(data.bands.battery.critical ?? 0)}
                  tone={(data.bands.battery.critical ?? 0) > 0 ? 'alert' : undefined}
                  foot="Under 15% — a flat band stops measuring"
                />
                <Kpi
                  label="Connectors erroring"
                  value={count(providers.reduce((sum, [, p]) => sum + p.erroring, 0))}
                  tone="orange"
                />
              </div>

              <div className="grid grid--2">
                <Card
                  title="Firmware distribution"
                  hint="A long tail here means the OTA rollout stalled"
                >
                  <BarChart
                    data={Object.entries(data.capsules.firmware).map(([version, n]) => ({
                      label: version,
                      value: n,
                    }))}
                    color="var(--cyan)"
                  />
                </Card>

                <Card title="Battery health" hint="Capsules grouped by remaining charge">
                  <BarChart
                    data={[
                      { label: 'healthy ≥40%', value: data.capsules.battery.healthy ?? 0 },
                      { label: 'low 15–39%', value: data.capsules.battery.low ?? 0 },
                      { label: 'critical <15%', value: data.capsules.battery.critical ?? 0 },
                    ]}
                    color="var(--lime)"
                  />
                </Card>
              </div>

              <Card
                title="Third-party connectors"
                hint="Whoop, Garmin, Fitbit, Oura and anything else granted through OAuth"
                flush
              >
                <Table
                  rows={providers.map(([provider, stats]) => ({ provider, ...stats }))}
                  rowKey={(row) => row.provider}
                  empty="No third-party sources connected by anyone yet."
                  columns={[
                    {
                      key: 'provider',
                      header: 'Provider',
                      strong: true,
                      render: (row) => titleCase(row.provider),
                    },
                    { key: 'connected', header: 'Connected', render: (row) => <span className="num">{row.connected}</span> },
                    {
                      key: 'expiring',
                      header: 'Token expiring',
                      render: (row) =>
                        row.expiring > 0 ? (
                          <Badge tone="orange">{row.expiring} within 7 days</Badge>
                        ) : (
                          <span className="dim">—</span>
                        ),
                    },
                    {
                      key: 'stale',
                      header: 'Stale sync',
                      render: (row) =>
                        row.stale > 0 ? (
                          <Badge tone="orange">{row.stale} &gt; 24 h</Badge>
                        ) : (
                          <span className="dim">—</span>
                        ),
                    },
                    {
                      key: 'erroring',
                      header: 'Erroring',
                      render: (row) =>
                        row.erroring > 0 ? (
                          <Badge tone="alert">{row.erroring}</Badge>
                        ) : (
                          <span className="dim">—</span>
                        ),
                    },
                    { key: 'disabled', header: 'Disabled', render: (row) => <span className="dim">{row.disabled}</span> },
                  ]}
                />
              </Card>

              <Card title="Other paired hardware" hint="Bands, NFC tags and phones">
                <div className="row row--wrap" style={{ gap: 18 }}>
                  {Object.keys(data.other_devices).length === 0 ? (
                    <span className="dim">Nothing besides Capsules is paired.</span>
                  ) : (
                    Object.entries(data.other_devices).map(([kind, n]) => (
                      <span key={kind}>
                        <span className="kpi__label">{titleCase(kind)}</span>
                        <div className="num" style={{ fontSize: 22 }}>
                          {n}
                        </div>
                      </span>
                    ))
                  )}
                </div>
              </Card>
            </>
          )
        }}
      </Loaded>
    </Page>
  )
}
